import { PlusIcon, X, MessageCircleMore, Logs } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import chopLogo from "@/assets/chop_logo.svg";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "../ui/button";
import { ChatContext } from "@/store/chat.store";
import { useContext, useMemo, useState, useEffect, useRef } from "react";
import useAuthService from "@/services/auth.service";
import { ProfileIcon } from "../profile/profileIcon";
import { NavigationMenu } from "./navigation-menu";
import { ChatHistory } from "./chat-history";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { useMsal } from "@azure/msal-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import useConfigService from "@/services/config.service";
import parse from "html-react-parser";
import { REPORT_ISSUE_URL } from "@/constants/app.constant";
import ModalUsageDropdown from "./modal-usage-dropdown";

/**
 * Props for the AppSidebar component
 */
interface Props {
}

/**
 * AppSidebar component displays the main application sidebar with navigation menu,
 * chat history, new chat button, user profile, and footer. Manages conversation
 * state, navigation, and user authentication. Shows different content for admin
 * and regular users.
 * 
 * @component
 * @param {Props} props - Component props (currently empty)
 * @returns {JSX.Element} Application sidebar with navigation and chat history
 * 
 * @example
 * ```tsx
 * <AppSidebar />
 * ```
 */
export function AppSidebar({  }: Props) {
  const { state, dispatch } = useContext(ChatContext);
  const { toggleSidebar, isMobile } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCurrentUserIsAdmin, logout } = useAuthService();
  const { accounts } = useMsal();
  const { fetchTermsAndConditions } = useConfigService();

  const isGenerating = state.isResponseGenerating;

  const handleNewChat = () => {
    if (isGenerating) return;
    dispatch({ field: "selectedConversation", value: null });
    const urlType = state.conversationType.toLowerCase().replace(/_/g, "-");
    navigate(`/chat/${urlType}`, { replace: true });
    dispatch({ field: "content", value: "" });

    if (isMobile) toggleSidebar();
  };

  const handleNavigationClick = (url: string, type?: string) => {
    if (type && state.conversationType !== type) {
      dispatch({ field: "selectedConversation", value: null });
      dispatch({ field: "conversationType", value: type });
    }
    navigate(url);
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  if (isMobile && window.location.pathname.search('admin') > 0) {
    navigate('/')
  }

  const isAdmin = getCurrentUserIsAdmin();
  const [showTC, setShowTC] = useState(false);
  const [tncContent, setContent] = useState("");

  const initialTab = useMemo(() => {
    return location.pathname.startsWith("/admin") ? "admin" : "chats";
  }, [location.pathname]);

  const [tabValue, setTabValue] = useState<string>(initialTab);

  useEffect(() => {
    const loadTnC = async () => {
      const response = await fetchTermsAndConditions();
      if (response?.data) {
        setContent(response.data.terms_and_conditions)
      }
    }
    loadTnC();
  }, [])

  useEffect(() => {
    const newTab = location.pathname.startsWith("/admin") ? "admin" : "chats";
    setTabValue(newTab);
  }, [location.pathname]);

  // handle user clicking a tab, update route accordingly
  const handleTabChange = (value: string) => {
    setTabValue(value);
    //Clear selected conversation
    dispatch({ field: "selectedConversation", value: null });

    if (value === "admin") {
      navigate("/admin/user-manager");
    } else {
      navigate("/chat/chop-gpt");
    }
  };

  return (
    <Sidebar className="flex flex-col">
      {/* Header */}
      <SidebarHeader className="py-2">
        <div className="flex items-center justify-between pl-1">
          <img src={chopLogo} alt="CHOP Logo" width={153} />
          <SidebarTrigger />
        </div>
      </SidebarHeader>

      {/* Conditional Toggle Panel */}
      {isAdmin && !isMobile? (
        // Controlled Tabs with onValueChange so user can toggle
        <Tabs value={tabValue} onValueChange={handleTabChange}>
          {/* Tabs Header */}
          <div className="px-2 py-2 flex justify-center">
            <TabsList className="w-[80%]">
              <TabsTrigger value="chats" className="w-[50%]   data-[state=inactive]:text-toggleInactive  data-[state=active]:text-primary-600">
                <MessageCircleMore className="mr-3" size={18} />
                Chats
              </TabsTrigger>
              <TabsTrigger value="admin" className="w-[50%] data-[state=inactive]:text-toggleInactive data-[state=active]:text-primary-600">
                <Logs className="mr-3" size={18} />
                Admin
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Chats Tab */}
          <TabsContent value="chats">
            <div className="px-4 py-2">
              <Button
                size="sm"
                className="bg-primary-500 text-primary-foreground font-medium text-sm w-full"
                onClick={handleNewChat}
                disabled={isGenerating}
              >
                <PlusIcon /> New Chat
              </Button>
            </div>

            <SidebarContent className="text-text-500 font-medium">
              <NavigationMenu
                conversationType={state.conversationType}
                onNavigationClick={handleNavigationClick}
                isGenerating={isGenerating}
              />

              <ChatHistory
                conversationType={state.conversationType}
                isLoadingHistory={state.isLoadingHistory}
                refetchFolders={state.refetchFolders}
              />
            </SidebarContent>
          </TabsContent>

          {/* Admin Tab */}
          <TabsContent value="admin">
            <SidebarContent className="text-text-500 font-medium">
              <NavigationMenu onNavigationClick={handleNavigationClick} isAdmin/>
            </SidebarContent>
          </TabsContent>
        </Tabs>
      ) : (
        // Default view when toggle is disabled (for non-admins)
        <>
          <div className="px-4 py-2">
            <Button
              size="sm"
              className="bg-primary-500 text-primary-foreground font-medium text-sm w-full"
              onClick={handleNewChat}
              disabled={isGenerating}
            >
              <PlusIcon /> New Chat
            </Button>
          </div>

          <SidebarContent className="text-text-500 font-medium">
            <NavigationMenu
              conversationType={state.conversationType}
              onNavigationClick={handleNavigationClick}
            />

            <ChatHistory
              conversationType={state.conversationType}
              isLoadingHistory={state.isLoadingHistory}
              refetchFolders={state.refetchFolders}
            />
          </SidebarContent>
        </>
      )}

      <Dialog open={showTC}>
        <DialogContent className="md:max-w-[60svw]">
          <DialogHeader><DialogTitle className="flex justify-between m-0 text-primary-500">
              Terms and Conditions
              <button
              onClick={()=>setShowTC(false)}
                aria-label="Close"
                className="rounded-lg hover:bg-gray-100 active:bg-gray-200"
              >
                <X className="h-5 w-5 text-secondary" />
              </button>
          </DialogTitle></DialogHeader>
          <div
            className="text-[#71717B] pr-2 md:pr-4 text-xs md:text-sm leading-relaxed font-secondary text-justify max-h-[60svh] overflow-y-auto [&_ul]:list-disc [&_ul]:pl-6 [&_a]:text-primary-500 [&_a]:underline hover:[&_a]:text-primary-600 [&_h3]:font-semibold [&_h3]:my-2"
          >
            {parse(tncContent)}
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <SidebarFooter className="mt-auto">
        <div className="flex w-full items-center p-1">
          <div>
            <ModalUsageDropdown />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Open user menu"
                className="rounded-full ml-auto"
              >
                <ProfileIcon />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44 z-[101]">
              <DropdownMenuLabel>
                {(() => {
                  const accountName =
                    accounts && accounts.length > 0
                      ? accounts[0]?.name || accounts[0]?.username
                      : null;

                  return (<span className="font-semibold text-primary-500 text-sm">
                      {accountName}
                  </span>);
                })()}
              </DropdownMenuLabel>
              <DropdownMenuSeparator></DropdownMenuSeparator>
              <DropdownMenuItem className="font-normal cursor-pointer" onClick={() => setShowTC(true)}>Terms & Conditions</DropdownMenuItem>
              {REPORT_ISSUE_URL && <a href={REPORT_ISSUE_URL} target="_blank"><DropdownMenuItem className="font-normal cursor-pointer">Report issue</DropdownMenuItem></a>}
              <DropdownMenuSeparator></DropdownMenuSeparator>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleSignOut();
                }}
                className="font-normal cursor-pointer"
              >
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
